/**
 * GxRoundClock (screens §2, ADR-0015): "Round 2 open · 38:12". Display only: nothing happens at zero, and a countdown that has
 * run out keeps showing 0:00. States: none / running / warn (≤ warning minutes) / paused / closed. Click opens the year's
 * quarter-end markers.
 */
import { useEffect, useRef, useState } from "react";
import type { ContentCatalog, RoundSummary } from "@bsim/contracts";
import { fiscalYearLabel } from "../format";
import { useLocation } from "./history";
import { GxPopover } from "../theme/Popover";
import { useNow } from "./useNow";

/** The chair is the way into round 1: while the player is on it the clock says so, never "Round 1 open" (the round opens for decisions when they begin it). */
export const BEFORE_ROUND_ONE = "Before round 1";

/** The round's state in words, the one source for the clock and every banner that names it. */
export const STATE_WORDS: Readonly<Record<RoundSummary["state"], string>> = {
  draft: "not open yet", open: "open", locked: "closed", resolving: "running the year", awaitingSurprise: "live surprise",
  resolvingPhase2: "running the year", resolved: "results ready", released: "results are in", paused: "paused",
};

/** "38:12", or "1:05:09" past an hour. */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1_000));
  const h = Math.floor(total / 3_600);
  const m = Math.floor((total % 3_600) / 60);
  const s = String(total % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
}

export type ClockState = "none" | "running" | "warn" | "paused" | "closed";

export function clockState(round: RoundSummary, warningMinutes: number, now: number): ClockState {
  if (round.state === "paused") return "paused";
  if (round.state !== "open" && round.state !== "awaitingSurprise") return "closed";
  if (round.countdownEndsAt === null) return "none";
  return Date.parse(round.countdownEndsAt) - now <= warningMinutes * 60_000 ? "warn" : "running";
}

/** `label` replaces "Round N open · countdown" while the player is before round 1 proper (Take the chair), so the bar agrees with the page. */
export function GxRoundClock({ round, warningMinutes, catalog, label, solo = false }: { readonly round: RoundSummary; readonly warningMinutes: number; readonly catalog: ContentCatalog | null; readonly label?: string; /** A solo run has no facilitator to "release" a round: its year's results are simply in. */ readonly solo?: boolean }) {
  const now = useNow();
  const onChair = /\/chair\/?$/.test(useLocation().pathname);
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  // The calendar is a click-open popover (a bottom sheet on a phone): it closes on Escape, on a click or focus outside it, never staying over the page.
  useEffect(() => {
    if (!open) return;
    const away = (e: Event) => { if (e.target instanceof Element && !wrap.current?.contains(e.target) && e.target.closest(".gx-popover, .gx-bsheet") === null) setOpen(false); };
    document.addEventListener("pointerdown", away); document.addEventListener("focusin", away);
    return () => { document.removeEventListener("pointerdown", away); document.removeEventListener("focusin", away); };
  }, [open]);
  const state = clockState(round, warningMinutes, now);
  const remaining = state === "paused" ? round.remainingMsWhenPaused : round.countdownEndsAt === null ? null : Date.parse(round.countdownEndsAt) - now;
  const text = state === "paused" ? "paused" : round.soloPhase === "awaiting_event" ? "paused for a card" : solo && round.state === "released" ? "results are in" : STATE_WORDS[round.state];
  const words = `Round ${round.roundNo} ${text}`;
  const left = remaining === null || state === "closed" ? null : `${formatCountdown(remaining)} left`;
  const full = label ?? (onChair && round.roundNo <= 1 ? BEFORE_ROUND_ONE : `${words}${left === null ? "" : ` · ${left}`}`);
  return (
    <div ref={wrap} style={{ position: "relative" }}>
      <button type="button" className="gx-clock gx-num" aria-label={full} data-state={state} data-phase={round.state === "released" || round.state === "resolved" ? "results" : "play"} data-testid="shell.clock" aria-expanded={open} onClick={() => setOpen((o) => !o)}
        title="Opens this year's calendar. The countdown is display only: nothing happens at zero.">
        <span className="gx-clock-dot" aria-hidden="true" />
        {label !== undefined || (onChair && round.roundNo <= 1) ? full : left === null ? <><span className="gx-clock-long">Round {round.roundNo} </span>{text}</> : <><span className="gx-clock-long">{words} · </span>{left}</>}
      </button>
      {open ? (
        <GxPopover anchor={wrap} label="This year's calendar" title={catalog === null ? `Round ${round.roundNo}` : fiscalYearLabel(catalog, round.roundNo)} align="end" onClose={() => setOpen(false)}>
          <b className="gx-pop-title">{catalog === null ? `Round ${round.roundNo}` : fiscalYearLabel(catalog, round.roundNo)}</b>
          <p>Quarter-ends fall in months 3, 6, 9 and 12 of the year. Events arrive in the month their card names.</p>
        </GxPopover>
      ) : null}
    </div>
  );
}
